import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from '../../../middleware/ThemeContext'
import PageCard from './PagesComponents/PageCard';
import SecTitleWithIconDesc from '../components/secTitleWithIconDesc';
import NewPageIcon from '../../../assets/icons/NewPageIcon';
import Badge from '../../../components/Badge';
import { Button } from '../../../components/form/Button';
import NewPageModal from './PagesComponents/NewPageModal';
import FilterIcon from '../../../assets/icons/FilterIcon';
import { CreateFormTheme, font, Theme } from '../../../utils/themes';
import StyledText from '../../../components/StyledComponents/StyledText';
import styled from 'styled-components';
import { useDeleteWebistePageMutation, useDuplicateWebistePageMutation, useGetPagesQuery, useGetPageTagsQuery } from '../../../api/pageBuilderApiSlice';
import { SkeletonCell } from '../../../components/SkeletonCell';
import ChevronDownIcon from '../../../assets/icons/ChevronDownIcon';
import DeletePrompt from '../../../components/DeletePrompt';
import { useToast } from '../../../components/toast/ToastContext';
import { useNavigate } from 'react-router-dom';
import DropdownButton from '../../../components/StyledComponents/StyledDropdownButton';
import NewFilterIcon from '../../../assets/icons/NewFilterIcon';
import { copyToClipboard, hexToRgba } from '../../../utils/helper';
import StyledCheckBox from '../../../components/StyledComponents/CheckBox/StyledCheckBox';
import SearchBar from '../../../components/searchBar/SearchBar';
import Pagination from '../../../components/listing/Pagination';
import ExpandableSearch from '../../../components/listing/ExpandableSearch';
import NoData from '../../../components/listing/NoData';

interface Tab {
    label: string;
    isActive: boolean;
}

// Single role interface
export interface PageRole {
    id: number;
    name: string;
    roleAccessKey: string;
    roleType: string; // "Custom" | "Editor" | "Viewer" etc.
}

// Page interface for editing
export interface EditablePage {
    id: number;
    visibility: "public" | "private"; // or string if there are other types
    name: string;
    tags: string; // comma-separated string, e.g., "general,marketing"
    url: string;
    websiteId: number;
    statusId: number;
    createdAt: string; // ISO date string
    updatedAt: string; // ISO date string
    roles: PageRole[]; // array of roles
    isStandard: boolean;
    rows?: any[]; // page rows with elements
    pageType: "internal" | "external"; // or string if there are other types
}



const Pages = ({ isResponsive, companyLoading, refreshToken, domain }: { isResponsive?: boolean, companyLoading?: boolean, refreshToken?: number, domain?: string }) => {
    const { theme } = useTheme();
    const showToast = useToast();
    const navigate = useNavigate();
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState<6 | 38 | null>(null);
    const [pageTypeFilter, setPageTypeFilter] = useState<"system" | "custom" | null>(null);
    const [visibilityFilter, setVisibilityFilter] = useState<"public" | "private" | "members-only" | null>(null);
    const [accessTypeFilter, setAccessTypeFilter] = useState<"internal" | "external" | null>(null);
    const [isEdit, setIsEdit] = useState<number | null>(null);
    const [isStandard, setIsStandard] = useState<boolean>(false);

    const [loading, setLoading] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [duplicatingPageId, setDuplicatingPageId] = useState<number | null>(null);
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean, pageId: number | null }>({ isOpen: false, pageId: null });
    const tagsDropdownRef = useRef<HTMLDivElement>(null);
    const [showTagsDropdown, setShowTagsDropdown] = useState(false);
    const [tagsSearch, setTagsSearch] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    };

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearchValue(searchValue.trim());
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [searchValue]);

    const { data: tags, refetch: tagsRefetch, isLoading: tagsLoading, isFetching: tagsFetching } = useGetPageTagsQuery(undefined, { refetchOnMountOrArgChange: true });
    const [deletePage] = useDeleteWebistePageMutation();
    const [duplicatePage] = useDuplicateWebistePageMutation();
    const [tabs, setTabs] = useState<Tab[]>([
        { label: "All Tags", isActive: true },
    ]);
    const isFilterApplied =
        statusFilter !== null ||
        pageTypeFilter !== null ||
        visibilityFilter !== null ||
        accessTypeFilter !== null;

    const selectedTagLabels = tabs
        .filter(tab => tab.label !== "All Tags" && tab.isActive)
        .map(tab => tab.label);

    // Prepare query param
    const tagQuery = selectedTagLabels.length > 0
        ? selectedTagLabels.map(tag => tag.toLowerCase()).join(',')
        : undefined;

    const isStandardFilter =
        pageTypeFilter === 'system' ? true :
            pageTypeFilter === 'custom' ? false : undefined;

    const handleClearFilters = () => {
        setStatusFilter(null);
        setVisibilityFilter(null);
        setPageTypeFilter(null);
        setAccessTypeFilter(null);
    };

    const { data, refetch, isLoading, isFetching } = useGetPagesQuery({
        tags: tagQuery,
        query: debouncedSearchValue || undefined,
        statusId: statusFilter ?? undefined,
        visibility: visibilityFilter ?? undefined,
        isStandard: isStandardFilter,
        pageType: accessTypeFilter ?? undefined,
        page: currentPage,
        perPage,
    }, { refetchOnMountOrArgChange: true });

    const totalItems = Number(data?.totalCount ?? 0);
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage));

    // Force a fresh API call on mount and when parent tab activation requests refresh.
    useEffect(() => {
        setCurrentPage(1);
        refetch();
        tagsRefetch();
    }, [refreshToken, refetch, tagsRefetch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [tagQuery, debouncedSearchValue, statusFilter, visibilityFilter, pageTypeFilter, accessTypeFilter]);

    useEffect(() => {
        if (!isLoading && !isFetching && currentPage > 1 && (data?.pages?.length ?? 0) === 0 && totalItems > 0) {
            setCurrentPage(prev => Math.max(1, prev - 1));
        }
    }, [data, currentPage, isFetching, isLoading, totalItems]);

    useEffect(() => {
        if (tags?.tags && Array.isArray(tags.tags)) {
            const previousState = new Map(tabs.map(tab => [tab.label, tab.isActive]));
            const tagTabs: Tab[] = tags.tags.map((tag: string) => ({
                label: tag.charAt(0).toUpperCase() + tag.slice(1),
                isActive: previousState.get(tag.charAt(0).toUpperCase() + tag.slice(1)) ?? false,
            }));

            const hasSelectedTag = tagTabs.some(tab => tab.isActive);

            setTabs([
                { label: "All Tags", isActive: !hasSelectedTag },
                ...tagTabs,
            ]);
        }
    }, [tags]);

    // const handleTabClick = (index: number) => {
    //     const newTabs = tabs.map((tab, i) => ({
    //         ...tab,
    //         isActive: i === index,
    //     }));
    //     setTabs(newTabs);
    // };

    const handleDelete = async (id: number) => {
        try {
            setLoading(true);
            await deletePage(id).unwrap();
            setCurrentPage(1);
            refetch();
            tagsRefetch();
        } catch (error: any) {
            // ✅ centralized error handling
            const message =
                error?.data?.message ||
                error?.error ||
                'Something went wrong while deleting website page';

            showToast('Error', message, 'error');

        } finally {
            setLoading(false);
            setDeleteModal({ isOpen: false, pageId: null });
        }
    };

    const handleDuplicate = async (id: number, pageType?: 'internal' | 'external') => {
        setIsDuplicating(true);
        setDuplicatingPageId(id);
        try {
            await duplicatePage(id).unwrap();
            if (pageType !== 'external') {
                setStatusFilter(6);
            }
            setCurrentPage(1);
            refetch();
            tagsRefetch();
        } catch (error: any) {
            // ✅ centralized error handling
            const message =
                error?.data?.message ||
                error?.error ||
                'Something went wrong while duplicate website page';

            showToast('Error', message, 'error');
            setIsDuplicating(false);
            setDuplicatingPageId(null);
        } finally {
            setIsDuplicating(false);
            setDuplicatingPageId(null);
        }
    };

    const handleTabClick = (label: string) => {
        setTabs(prevTabs => {
            if (label === 'All Tags') {
                return prevTabs.map(tab => ({ ...tab, isActive: tab.label === 'All Tags' }));
            }

            const updatedTabs = prevTabs.map(tab =>
                tab.label === label ? { ...tab, isActive: !tab.isActive } : tab
            );

            const hasSelectedTag = updatedTabs.some(tab => tab.label !== 'All Tags' && tab.isActive);

            return updatedTabs.map(tab =>
                tab.label === 'All Tags' ? { ...tab, isActive: !hasSelectedTag } : tab
            );
        });
    };

    const sortedTabs = (() => {
        const allPagesTab = tabs.find(tab => tab.label === 'All Tags');
        const otherTabs = tabs.filter(tab => tab.label !== 'All Tags');

        otherTabs.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
        );

        return allPagesTab ? [allPagesTab, ...otherTabs] : otherTabs;
    })();

    const visibleTabs = sortedTabs.slice(0, 5);
    const hiddenCount = Math.max(sortedTabs.length - visibleTabs.length, 0);
    const searchableTabs = tagsSearch.trim().length > 0
        ? sortedTabs.filter(tab => tab.label.toLowerCase().includes(tagsSearch.toLowerCase()))
        : sortedTabs;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(event.target as Node)) {
                setShowTagsDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);



    return (
        <PagesContainer theme={theme}>
            {/* Header */}
            <Header className="d-flex align-items-center justify-content-between" theme={theme}>
                <SecTitleWithIconDesc
                    fontWeight={700}
                    fontSize={isResponsive ? "16px" : "18px"}
                    fontColor={theme.CorporateTheme.colors.primary}
                    descfontSize={isResponsive ? "12px" : "16px"}
                    icon={
                        <NewPageIcon
                            size={isResponsive ? 22 : 32}
                            color={theme.CorporateTheme.colors.primary}
                        />
                    }
                    title="All Pages"
                    desc="All predefined, custom and module based pages will be listed here"
                />

                <Button
                    theme={theme}
                    btnBg={theme.CorporateTheme.colors.primary}
                    color={theme.CorporateTheme.colors.background}
                    type="button"
                    $variant="customButton"
                    className="px-4"
                    borderColor="none"
                    onClick={() => setShowCreateModal(true)}
                >
                    Create New Page
                </Button>
            </Header>

            <div className="d-flex align-items-center justify-content-between p-4 gap-4">

                <TabsContainer>
                    <TabsWrapper>
                        {tagsLoading || tagsFetching ? (
                            Array.from({ length: 5 }, (_, index) => (
                                <SkeletonCell
                                    key={index}
                                    height="36px"
                                    width="90px"
                                    className="rounded-pill"
                                />
                            ))
                        ) : (
                            visibleTabs.map((tab) => (
                                <Badge
                                    key={tab.label}
                                    onClick={() => handleTabClick(tab.label)}
                                    borderColor={
                                        tab.isActive
                                            ? theme.CreateFormTheme.colors.count
                                            : theme.CreateFormTheme.colors.inputBorders
                                    }
                                    bgColor={
                                        tab.isActive
                                            ? theme.CorporateTheme.colors.count
                                            : 'transparent'
                                    }
                                    color={
                                        tab.isActive
                                            ? theme.CorporateTheme.colors.white
                                            : theme.CorporateTheme.colors.headerColor
                                    }
                                    fontSize="12px"
                                    fontWeight={600}
                                    text={tab.label}
                                    borderRadius="28px"
                                    padding="7px 18px"
                                    cursor="pointer"
                                />
                            ))
                        )}

                        {!tagsLoading && !tagsFetching && hiddenCount > 0 && (
                            <MoreTagsWrapper ref={tagsDropdownRef}>
                                <MoreTagsButton
                                    type="button"
                                    theme={theme}
                                    onClick={() => setShowTagsDropdown(prev => !prev)}
                                >
                                    <StyledText fontSize='10px' fontFamily={font.lato} textColor={theme.CorporateTheme.colors.graniteGrey}>+{hiddenCount}</StyledText>
                                </MoreTagsButton>

                                {showTagsDropdown && (
                                    <TagsDropdownContainer theme={theme}>
                                        <SearchBar
                                            placeholder="Search tags here"
                                            onSearchChange={(value) => setTagsSearch(value)}
                                            searchTerm={tagsSearch}
                                            style={{ width: '100%', marginBottom: 10 }}

                                        />

                                        <TagsDropdownGrid>
                                            {searchableTabs.map(tab => (
                                                <TagsDropdownItem key={tab.label} theme={theme} isActive={tab.isActive}>
                                                    {/* <input
                                                        type="checkbox"
                                                        checked={tab.isActive}
                                                        onChange={() => handleTabClick(tab.label)}
                                                    />
                                                    <span>{tab.label}</span> */}

                                                    <StyledCheckBox
                                                        value={tab.isActive}
                                                        onChange={() => handleTabClick(tab.label)}
                                                        // onClick={handleCheckboxToggle}

                                                        size={12}
                                                        borderColor={tab.isActive ? theme.CreateFormTheme.colors.primary : theme.CreateFormTheme.colors.headerColor}
                                                        bgColor={tab.isActive ? theme.CreateFormTheme.colors.primary : 'transparent'}
                                                        checkColor={theme.CorporateTheme.colors.background}
                                                        color={theme.CreateFormTheme.colors.textSecondary}
                                                        className="w-auto me-0 me-sm-0 flex-shrink-0"
                                                        label={tab.label}
                                                        labelSize='12px'
                                                    />
                                                </TagsDropdownItem>
                                            ))}
                                        </TagsDropdownGrid>
                                    </TagsDropdownContainer>
                                )}
                            </MoreTagsWrapper>
                        )}
                    </TabsWrapper>
                </TabsContainer>

                <div className='d-flex align-items-center gap-2'>
                    <ExpandableSearch
                        disabled={loading || isFetching}
                        value={searchValue}
                        onChange={handleSearchChange}
                    />

                    <StatusWrapper>
                        <FilterIconWrapper theme={theme} hasActiveFilter={isFilterApplied}>
                            <NewFilterIcon
                                size={12}
                                color={theme.CorporateTheme.colors.graniteGrey}
                            />
                        </FilterIconWrapper>


                        <DropdownButton
                            iconPosition='right'
                            icon={<ChevronDownIcon color={theme.CorporateTheme.colors.headerColor} size={12} />}
                            buttonLabel="Filters"
                            align="right"
                            removeborder
                            menuFontWeight={500}
                            items={[
                                {
                                    label: "Clear Filters",
                                    onClick: handleClearFilters,
                                    isSelected: !isFilterApplied
                                },
                                { label: "", isSeparator: true },
                                { label: "Publish", onClick: () => setStatusFilter(38), isSelected: statusFilter === 38 },
                                { label: "Draft", onClick: () => setStatusFilter(6), isSelected: statusFilter === 6 },
                                { label: "", isSeparator: true },
                                {
                                    label: "Public",
                                    onClick: () => setVisibilityFilter(prev => (prev === "public" ? null : "public")),
                                    isSelected: visibilityFilter === "public"
                                },
                                {
                                    label: "Private",
                                    onClick: () => setVisibilityFilter(prev => (prev === "private" ? null : "private")),
                                    isSelected: visibilityFilter === "private"
                                },
                                {
                                    label: "Members Only",
                                    onClick: () => setVisibilityFilter(prev => (prev === "members-only" ? null : "members-only")),
                                    isSelected: visibilityFilter === "members-only"
                                },
                                { label: "", isSeparator: true },
                                {
                                    label: "System Pages",
                                    onClick: () => setPageTypeFilter(prev => (prev === "system" ? null : "system")),
                                    isSelected: pageTypeFilter === "system"
                                },
                                {
                                    label: "Custom Pages",
                                    onClick: () => setPageTypeFilter(prev => (prev === "custom" ? null : "custom")),
                                    isSelected: pageTypeFilter === "custom"
                                },
                                { label: "", isSeparator: true },
                                {
                                    label: "Internal",
                                    onClick: () => setAccessTypeFilter(prev => (prev === "internal" ? null : "internal")),
                                    isSelected: accessTypeFilter === "internal"
                                },
                                {
                                    label: "External",
                                    onClick: () => setAccessTypeFilter(prev => (prev === "external" ? null : "external")),
                                    isSelected: accessTypeFilter === "external"
                                },

                            ]}
                        />
                    </StatusWrapper>
                </div>
            </div>

            {/* Cards */}
            <CardsGrid>
                {isLoading || isFetching ?

                    Array.from({ length: 3 }).map((_, index) => (
                        <SkeletonCell key={index}
                            height={'300px'} width={'100%'} style={{ maxWidth: 600, borderRadius: 16 }} />
                    )) :
                    <>
                        {(data?.pages?.length ?? 0) > 0 ? (
                            data?.pages?.map((item: EditablePage, i: number) => (
                                <PageCard
                                    key={i}
                                    title="Driven By Passion, Powered By Innovation"
                                    subtitle="Discover our story, our values and the people behind the creativity."
                                    buttonText="Learn More"
                                    pageName={item?.name}
                                    publishedDate={item?.createdAt}
                                    onEditPage={() => { setIsEdit(item?.id); setShowCreateModal(true); setIsStandard(item?.isStandard); }}
                                    onEditPageSetting={() => navigate(`/home/page-builder/${item?.id}`)}
                                    onDuplicate={() => handleDuplicate(item?.id, item?.pageType)}
                                    onDelete={() => { setDeleteModal({ isOpen: true, pageId: item?.id }); }}
                                    onCopyPageURL={() => copyToClipboard(item?.pageType !== 'external' ? `https://${domain}/?page=${item.url}` : item.url)}
                                    theme={theme}
                                    isStandard={item?.isStandard}
                                    pageType={item?.pageType}
                                    visibility={item?.visibility}
                                    duplicatingPageId={duplicatingPageId === item?.id}
                                    pageData={item}
                                    customCss={data?.websiteSettings?.metaData?.customCodeSetting?.customCss}
                                    headerHtml={data?.websiteSettings?.metaData?.customCodeSetting?.headerHtml}
                                    primaryFont={data?.websiteSettings?.metaData?.font?.primaryFont}
                                    colorSettings={data?.websiteSettings?.metaData?.colorSettings}
                                />
                            ))
                        ) : (
                            <NoDataWrapper>
                                <NoData
                                    title='No Pages Available'
                                    subtitle='No page is available yet. Create a new page to get started.'
                                    containerHeight={475}
                                />
                            </NoDataWrapper>
                        )}
                    </>}
            </CardsGrid>

            <NewPageModal
                isOpen={showCreateModal}
                onClose={() => { setShowCreateModal(false); setIsEdit(null); setIsStandard(false); }}
                onSuccess={() => { setCurrentPage(1); refetch(); tagsRefetch(); setIsEdit(null); }}
                editPageId={isEdit}
                isSystemWidget={isStandard}
            />

            <PaginationWrapper>
                <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    perPage={perPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        if (page >= 1 && page <= totalPages) {
                            setCurrentPage(page);
                        }
                    }}
                    onLimitChange={(limit) => {
                        setPerPage(limit);
                        setCurrentPage(1);
                    }}
                />
            </PaginationWrapper>

            {deleteModal?.isOpen ? <DeletePrompt
                title='Are you sure you want to delete this page?'
                subTitle='Deleting this page will remove all associated data, including events, certifications, memberships, and more.'
                loading={loading}
                onConfirm={() => deleteModal.pageId !== null && handleDelete(deleteModal.pageId)}
                onCancel={() => setDeleteModal({ isOpen: false, pageId: null })}
            /> : null}
        </PagesContainer>
    )
}





const PagesContainer = styled.div<{ theme: Theme }>`
  background-color: ${({ theme }) =>
        theme.CorporateTheme.colors.background};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 10px;
  padding-bottom:20px;
`;

const Header = styled.div<{ theme: Theme }>`
  padding: 25px;
  border-bottom: 1px solid
    ${({ theme }) => theme.CorporateTheme.colors.border};
`;

// const TabsWrapper = styled.div`
//   display: flex;
//   gap: 12px;
//   flex-wrap: wrap;
//   align-items: center;
//   cursor: pointer;
// `;

const TabsContainer = styled.div`
  position: relative;
    flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
    flex-wrap: wrap;
    min-height: 43px;
`;

const MoreTagsButton = styled.button<{ theme: Theme }>`
    height: 30px;
    width: 30px;
    border-radius: 20px;
    border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
 
    padding: 0 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
  }
`;

const MoreTagsWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`;

const TagsDropdownContainer = styled.div<{ theme: Theme }>`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 30;
    width: min(418px, 90vw);
    max-height: 340px;
    overflow-y: auto;
    border-radius:0px 0px 10px 10px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
    padding: 10px;
`;


const TagsDropdownGrid = styled.div`
    // display: grid;
    // grid-template-columns: repeat(3, minmax(0, 1fr));
    // gap: 8px;

    // @media (max-width: 900px) {
    //     grid-template-columns: repeat(2, minmax(0, 1fr));
    // }

    // @media (max-width: 640px) {
    //     grid-template-columns: 1fr;
    // }
    display: flex;
    gap:8px;
    flex-wrap: wrap;
`;

const TagsDropdownItem = styled.label<{ theme: Theme, isActive?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 30px;
    min-width: 124px;
    border-radius: 18px;
    border: 1px solid ${({ isActive, theme }) =>
        isActive ? theme.CorporateTheme.colors.primary : theme.CorporateTheme.colors.border};
    background-color: ${({ isActive, theme }) =>
        isActive ? hexToRgba(theme.CorporateTheme.colors.primary, 0.1) : 'transparent'};
    padding: 4px 10px;
    font-size: 14px;
    color: ${({ theme }) => theme.CorporateTheme.colors.headerColor};
    cursor: pointer;

    input {
        margin: 0;
        accent-color: ${({ theme }) => theme.CorporateTheme.colors.count};
    }

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;


const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
    flex-shrink: 0;   /* 🔥 never shrink */

`;

const FilterIconWrapper = styled.div<{ theme: Theme, hasActiveFilter?: boolean }>`
    position: relative;
  height: 24px;
  width: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) =>
        theme.CorporateTheme.colors.filterBG};
  border: 1px solid
    ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  display: flex;
  align-items: center;
  justify-content: center;

    &::after {
        content: '';
        position: absolute;
        top: -2px;
        right: -2px;
        height: 8px;
        width: 8px;
        border-radius: 50%;
        background-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
        border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.background};
        opacity: ${({ hasActiveFilter }) => (hasActiveFilter ? 1 : 0)};
        transition: opacity 0.2s ease;
    }
`;

const Divider = styled.div<{ theme: Theme }>`
  height: 20px;
  width: 1px;
  background-color: ${({ theme }) =>
        theme.CreateFormTheme.colors.border};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(425px, 1fr));
  gap: 24px;
  padding: 0 25px;
`;

const NoDataWrapper = styled.div`
    grid-column: 1 / -1;
`;

const PaginationWrapper = styled.div`
    padding: 16px 25px 0 25px;
`;


export default Pages
